import React, { useState, useEffect, useCallback } from 'react';

import { IoClose } from 'react-icons/io5';

import { messages } from '@/features/dashboard/localization/texts';

import { PasswordStrengthMeterProps } from './interface';

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({ password }) => {
  const [strength, setStrength] = useState<number>(0);
  const [criteria, setCriteria] = useState({
    length: false,
    upperCase: false,
    lowerCase: false,
    number: false,
    specialChar: false,
  });

  const evaluatePasswordStrength = useCallback((pass: string): number => {
    let score = 0;
    if (!pass) {
      return score;
    }

    const lengthCriteria = pass.length >= 8;
    const upperCaseCriteria = /[A-Z]/.test(pass);
    const lowerCaseCriteria = /[a-z]/.test(pass);
    const numberCriteria = /\d/.test(pass);
    const specialCharCriteria = /[!@#$%^&*(),.?":{}|<>]/.test(pass);

    setCriteria({
      length: lengthCriteria,
      upperCase: upperCaseCriteria,
      lowerCase: lowerCaseCriteria,
      number: numberCriteria,
      specialChar: specialCharCriteria,
    });

    const lengthScore = Math.min(20, pass.length * 2);
    const upperCaseScore = /[A-Z]/.test(pass) ? 10 : 0;
    const numbersScore = /\d/.test(pass) ? 10 : 0;
    const specialCharScore = /[!@#$%^&*(),.?":{}|<>]/.test(pass) ? 10 : 0;
    const lowerCaseScore = /[a-z]/.test(pass) ? 10 : 0;

    score = lengthScore + upperCaseScore + numbersScore + specialCharScore + lowerCaseScore;
    return score;
  }, []);

  useEffect(() => {
    setStrength(evaluatePasswordStrength(password));
  }, [password, evaluatePasswordStrength]);

  const getProgressColor = (score: number): string => {
    if (score < 20) return 'bg-danger-500';
    if (score < 40) return 'bg-warning-500';
    if (score < 60) return 'bg-warning-400';
    return 'bg-success-500';
  };
  const createPassLabel = (score: number): string => {
    if (score < 20) return messages.passwordStrength.veryWeak;
    if (score < 40) return messages.passwordStrength.weak;
    if (score < 60) return messages.passwordStrength.fair;
    return messages.passwordStrength.strong;
  };
  const progressBarStyle = {
    width: `${(Math.min(strength, 60) * 100) / 60}%`,
    heigth: '4px',
    borderRadius: '10px',
  };
  if (!password) {
    return null;
  }
  return (
    <div>
      <p className="text-sm text-center mt-1 text-default-50 font-semibold">{messages.passwordStrength.title}</p>
      <div className="w-full bg-gray-200 h-1.5">
        <div className={`h-1.5 ${getProgressColor(strength)}`} style={progressBarStyle} />
      </div>
      <p className="text-xs text-center mt-1 font-semibold text-default-50">{createPassLabel(strength)}</p>
      <ul className="mt-2 text-xs text-default-50">
        <li className="flex items-center mt-1">
          {criteria.upperCase ? (
            <span className="text-success-500 text-xs mr-2 icon icon-check" />
          ) : (
            <IoClose className="text-danger-500 text-xs mr-2" />
          )}
          <span className="text-default-50">{messages.passwordStrength.criteria.uppercase}</span>
        </li>
        <li className="flex items-center mt-1">
          {criteria.lowerCase ? (
            <span className="text-success-500 text-xs mr-2 icon icon-check" />
          ) : (
            <IoClose className="text-danger-500 text-xs mr-2" />
          )}
          <span className="text-default-50">{messages.passwordStrength.criteria.lowercase}</span>
        </li>
        <li className="flex items-center mt-1">
          {criteria.number ? (
            <span className="text-success-500 text-xs mr-2 icon icon-check" />
          ) : (
            <IoClose className="text-danger-500 text-xs mr-2" />
          )}
          <span className="text-default-50">{messages.passwordStrength.criteria.number}</span>
        </li>
        <li className="flex items-center mt-1">
          {criteria.specialChar ? (
            <span className="text-success-500 text-xs mr-2 icon icon-check" />
          ) : (
            <IoClose className="text-danger-500 text-xs mr-2" />
          )}
          <span className="text-default-50">{messages.passwordStrength.criteria.specialChar}</span>
        </li>
        <li className="flex items-center mt-1">
          {criteria.length ? (
            <span className="text-success-500 text-xs mr-2 icon icon-check" />
          ) : (
            <IoClose className="text-danger-500 text-xs mr-2" />
          )}
          <span className="text-default-50">{messages.passwordStrength.criteria.length}</span>
        </li>
      </ul>
    </div>
  );
};
export default PasswordStrengthMeter;
